export type CreativeKind = 'image' | 'video'

export type AnalysisStatus =
  'pending' | 'labelling' | 'analysing' | 'cancelling' | 'done' | 'failed' | 'cancelled'

export const ANALYSIS_ACTIVE_STATUSES = [
  'pending',
  'labelling',
  'analysing',
  'cancelling',
] as const satisfies readonly AnalysisStatus[]

export const ANALYSIS_TERMINAL_STATUSES = [
  'done',
  'failed',
  'cancelled',
] as const satisfies readonly AnalysisStatus[]

export type AnalysisType = 'overall' | 'launch' | 'combination'
export type AnalysisWriteType = AnalysisType | 'single'

/** Production holds both predicate-list and legacy object-map filters. Writers use the list. */
export interface AnalysisFilterPredicate {
  field: string
  value: unknown
  operator?: string
  match_mode?: string
  [key: string]: unknown
}

export type AnalysisFilters = AnalysisFilterPredicate[] | Record<string, unknown>

/** The fixed 24-field core shared by list, create, update, and detail responses. */
export interface CreativeAiAnalysis {
  id: number
  workspace: number
  creator: number | null
  labeling_run: number | null
  status: AnalysisStatus
  total_count: number
  labelled_count: number
  skipped_count: number
  failed_count: number
  /** The run stopped because the balance ran out, not because creatives couldn't be labelled. */
  credit_exhausted: boolean
  untried_count: number
  failure_reason: string | null
  is_stale: boolean
  analyzed_image_count: number
  analyzed_video_count: number
  name: string
  creative_type: CreativeKind
  analysis_type: AnalysisType
  date_from: string
  date_to: string
  filters: AnalysisFilters
  selected_labels: string[]
  created_at: string
  updated_at: string
}

export interface CreativeAiAnalysisDetail extends CreativeAiAnalysis {
  brief_segment_count: number
  brief_credit_rate: number
}

export interface CreativeAiAnalysisListResponse {
  analyses: CreativeAiAnalysis[]
  image_label_count: number
  video_label_count: number
}

export interface CreativeAiAnalysisInput {
  name?: string
  creative_type: CreativeKind
  analysis_type?: AnalysisWriteType
  date_from: string
  date_to: string
  filters?: AnalysisFilterPredicate[]
  selected_labels?: string[]
}

export interface AnalysisDraft {
  name: string
  type: CreativeKind
  dateFrom: string
  dateTo: string
  effectiveStatus: string[]
  channel: string[]
  objective: string[]
  campaignType: string[]
  specificCampaigns: string[]
  minSpend: number | null
  activeDays: number | null
  additionalFilters?: AnalysisFilterPredicate[]
  preservedFilters?: AnalysisFilterPredicate[]
  /**
   * The recommendation's ORIGINAL predicates when this draft was seeded from one (v3
   * `prefillSourceFilters`). `analysisDraftFilters` folds them back over the rebuilt filters so a
   * field the user did not touch keeps the match mode the engine authored — see
   * `service/recommendations.mergePrefillFilters`. Absent for a blank or edit draft.
   */
  sourceFilters?: AnalysisFilterPredicate[]
}
